/**
 * The data table.
 *
 * Most of this console is tables, so this component carries the weight of the
 * redesign:
 *
 *  - Every column has a real header. The product being replaced had seven
 *    icon-only columns (Info, Detail, Action, Lock, Events,
 *    Commission) whose meanings were unguessable and unhoverable.
 *  - Numeric columns are right-aligned and tabular by construction, not by each
 *    caller remembering to ask.
 *  - The totals row is sticky at the top of the body, so scrolling 120 rows
 *    never loses the aggregate.
 *  - Sorting is keyboard-reachable and announced.
 *  - Loading renders skeleton rows at the real row height, so nothing jumps.
 */

import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Checkbox } from './form'
import { Icon } from './Icon'
import { Skeleton, StateBlock, cx } from './core'
import { useXScroll } from './useXScroll'

export type Align = 'left' | 'right' | 'center'

export interface Column<Row> {
  id: string
  header: ReactNode
  /** Cell renderer. Return a string/number for plain text. */
  cell: (row: Row, index: number) => ReactNode
  align?: Align
  width?: number | string
  /** Sort key. Omit to make the column unsortable. */
  sortValue?: (row: Row) => number | string | null
  /** Renders in the sticky totals row. */
  total?: ReactNode
  /** Numeric styling — right align + tabular numerals. */
  numeric?: boolean
  /** Hidden below this viewport width, in px. */
  hideBelow?: number
  /** Extra header tooltip explaining what the column means. */
  help?: string
}

export interface DataTableProps<Row> {
  rows: Row[]
  columns: Column<Row>[]
  rowKey: (row: Row, index: number) => string
  density?: 'compact' | 'default' | 'relaxed'
  /** Omit to inherit Appearance → Table density (`html[data-table-density]`). */
  loading?: boolean
  /** Rows rendered while loading. */
  skeletonRows?: number
  empty?: ReactNode
  onRowClick?: (row: Row) => void
  /** Renders on hover at the row end, over the last column. */
  rowActions?: (row: Row) => ReactNode
  selectable?: {
    selected: Set<string>
    onChange: (next: Set<string>) => void
    /**
     * Rows that may not be selected still render a checkbox — disabled, with a
     * reason. Removing the box instead would break the column and the rows
     * would stop scanning as a grid.
     */
    isSelectable?: (row: Row, key: string) => boolean
    /** Why this row cannot be selected. Becomes the disabled checkbox's accessible name. */
    disabledReason?: (row: Row) => string
    /**
     * Accessible name of a selectable row's checkbox. "Select row 3" is
     * unusable when the confirm dialog is about to move ₹4,62,820 out of that
     * row; it has to say "Select ravi_07".
     */
    rowLabel?: (row: Row, index: number) => string
  }
  /** Show the totals row. Columns without a `total` render blank. */
  showTotals?: boolean
  totalsLabel?: string
  initialSort?: { columnId: string; direction: 'asc' | 'desc' }
  /** Caption for screen readers — always supply one. */
  label: string
  /** Sticky header offset when the table sits under a toolbar. */
  stickyOffset?: number
  /**
   * Pin the rightmost column while the table scrolls sideways (default true —
   * downline row actions). Set false when the last column is ordinary data
   * (e.g. Bet List IP) and should scroll with the rest.
   */
  pinLastColumn?: boolean
  /** Row highlight — e.g. a member over their limit. */
  rowTone?: (row: Row) => 'loss' | 'warn' | undefined
  maxHeight?: number | string
}

export function DataTable<Row>({
  rows,
  columns,
  rowKey,
  density,
  loading = false,
  skeletonRows = 8,
  empty,
  onRowClick,
  rowActions,
  selectable,
  showTotals = false,
  totalsLabel = 'Total',
  initialSort,
  label,
  stickyOffset,
  pinLastColumn = true,
  rowTone,
  maxHeight,
}: DataTableProps<Row>) {
  // Above the early return below: hooks cannot run conditionally.
  const xs = useXScroll<HTMLDivElement>()
  const [sort, setSort] = useState<{ columnId: string; direction: 'asc' | 'desc' } | null>(
    initialSort ?? null,
  )

  const sorted = useMemo(() => {
    if (!sort) return rows
    const column = columns.find((c) => c.id === sort.columnId)
    if (!column?.sortValue) return rows
    const factor = sort.direction === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = column.sortValue!(a)
      const bv = column.sortValue!(b)
      // Nulls always sink, whichever way the column is sorted — an unknown
      // value is not "the smallest value".
      if (av === null && bv === null) return 0
      if (av === null) return 1
      if (bv === null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor
      return String(av).localeCompare(String(bv), 'en-IN') * factor
    })
  }, [rows, columns, sort])

  const toggleSort = (column: Column<Row>) => {
    if (!column.sortValue) return
    setSort((current) =>
      current?.columnId === column.id
        ? current.direction === 'desc'
          ? { columnId: column.id, direction: 'asc' }
          : null
        : { columnId: column.id, direction: 'desc' },
    )
  }

  const allKeys = sorted.map((row, i) => rowKey(row, i))

  /**
   * Select-all is computed over the SELECTABLE keys of this page only, and it
   * unions rather than replaces.
   *
   * Two bugs live in the obvious implementation. Replacing the set means
   * selecting on page 1 and then toggling the header on page 2 silently
   * discards page 1 — with money on the line, a selection that vanishes without
   * a word is the worst kind of defect. And computing `allSelected` over every
   * key means a page holding one un-selectable row leaves the header box
   * permanently indeterminate, so the operator can never tell whether they have
   * finished ticking.
   */
  const selectableKeys = selectable
    ? allKeys.filter((key, i) => selectable.isSelectable?.(sorted[i]!, key) ?? true)
    : []
  const allSelected =
    selectableKeys.length > 0 && selectableKeys.every((k) => selectable!.selected.has(k))
  const someSelected = selectableKeys.some((k) => selectable!.selected.has(k))

  const toggleAll = (next: boolean) => {
    if (!selectable) return
    const copy = new Set(selectable.selected)
    for (const key of selectableKeys) {
      if (next) copy.add(key)
      else copy.delete(key)
    }
    selectable.onChange(copy)
  }

  if (!loading && rows.length === 0) {
    return (
      <div className="w7-tablewrap">
        {empty ?? <StateBlock icon="search" title="Nothing matches these filters" />}
      </div>
    )
  }

  return (
    <div
      className="w7-tablewrap"
      ref={xs.ref}
      data-xscroll={xs.xScroll}
      data-pin-last={pinLastColumn ? undefined : 'false'}
      style={{
        maxHeight,
        ...(stickyOffset != null
          ? ({ ['--w7-table-sticky-top']: `${stickyOffset}px` } as CSSProperties)
          : null),
      }}
    >
      <table className="w7-table" data-density={density}>
        <caption className="w7-sr">{label}</caption>
        <thead>
          <tr>
            {selectable ? (
              <th style={{ width: 36 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  label={allSelected ? 'Clear the rows on this page' : 'Select every selectable row on this page'}
                  disabled={selectableKeys.length === 0}
                  onChange={toggleAll}
                />
              </th>
            ) : null}
            {columns.map((column) => {
              const active = sort?.columnId === column.id
              const alignment = column.align ?? (column.numeric ? 'right' : 'left')
              return (
                <th
                  key={column.id}
                  style={{ width: column.width, textAlign: alignment }}
                  data-hide-below={column.hideBelow}
                  aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  title={column.help}
                >
                  {column.sortValue ? (
                    <button
                      type="button"
                      className="w7-th-sort"
                      onClick={() => toggleSort(column)}
                      data-align={alignment}
                    >
                      {column.header}
                      <Icon
                        name={active ? (sort!.direction === 'asc' ? 'chevron-up' : 'chevron-down') : 'chevron-down'}
                        size={12}
                        style={{ opacity: active ? 1 : 0.28 }}
                      />
                    </button>
                  ) : (
                    <span className="w7-row" style={{ justifyContent: alignment === 'right' ? 'flex-end' : undefined }}>
                      {column.header}
                      {column.help ? <Icon name="info" size={12} style={{ opacity: 0.4 }} /> : null}
                    </span>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {showTotals && !loading ? (
            <tr className="w7-tr-total">
              {selectable ? <td /> : null}
              {columns.map((column, i) => (
                <td
                  key={column.id}
                  style={{ textAlign: column.align ?? (column.numeric ? 'right' : 'left') }}
                  className={cx(column.numeric && 'w7-mono')}
                  data-hide-below={column.hideBelow}
                >
                  {i === 0 && column.total === undefined ? totalsLabel : column.total}
                </td>
              ))}
            </tr>
          ) : null}

          {loading
            ? Array.from({ length: skeletonRows }, (_, i) => (
                <tr key={`skeleton-${i}`} aria-hidden="true">
                  {selectable ? <td /> : null}
                  {columns.map((column) => (
                    <td key={column.id} data-hide-below={column.hideBelow}>
                      <Skeleton width={column.numeric ? '60%' : '80%'} />
                    </td>
                  ))}
                </tr>
              ))
            : sorted.map((row, index) => {
                const key = rowKey(row, index)
                const tone = rowTone?.(row)
                return (
                  <tr
                    key={key}
                    data-tone={tone}
                    data-clickable={onRowClick ? 'true' : undefined}
                    data-selected={selectable?.selected.has(key) ? 'true' : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              onRowClick(row)
                            }
                          }
                        : undefined
                    }
                  >
                    {selectable ? (
                      (() => {
                        const can = selectable.isSelectable?.(row, key) ?? true
                        return (
                          <td onClick={(event) => event.stopPropagation()}>
                            <Checkbox
                              checked={can && selectable.selected.has(key)}
                              disabled={!can}
                              label={
                                can
                                  ? (selectable.rowLabel?.(row, index) ?? `Select row ${index + 1}`)
                                  : (selectable.disabledReason?.(row) ?? `Row ${index + 1} cannot be selected`)
                              }
                              onChange={(next) => {
                                const copy = new Set(selectable.selected)
                                if (next) copy.add(key)
                                else copy.delete(key)
                                selectable.onChange(copy)
                              }}
                            />
                          </td>
                        )
                      })()
                    ) : null}
                    {columns.map((column, ci) => (
                      <td
                        key={column.id}
                        style={{ textAlign: column.align ?? (column.numeric ? 'right' : 'left') }}
                        className={cx(column.numeric && 'w7-mono')}
                        data-hide-below={column.hideBelow}
                      >
                        {ci === columns.length - 1 && rowActions ? (
                          <span
                            className="w7-cell-with-actions"
                            style={{
                              justifyContent:
                                (column.align ?? (column.numeric ? 'right' : 'left')) === 'center'
                                  ? 'center'
                                  : (column.align ?? (column.numeric ? 'right' : 'left')) === 'left'
                                    ? 'flex-start'
                                    : 'flex-end',
                            }}
                          >
                            <span className="w7-cell-value">{column.cell(row, index)}</span>
                            <span className="w7-rowactions" onClick={(e) => e.stopPropagation()}>
                              {rowActions(row)}
                            </span>
                          </span>
                        ) : (
                          column.cell(row, index)
                        )}
                      </td>
                    ))}
                  </tr>
                )
              })}
        </tbody>
      </table>
      <span className="w7-sr" aria-live="polite">
        {loading ? 'Loading rows' : `${rows.length} rows`}
        {sort ? `, sorted by ${sort.columnId} ${sort.direction === 'asc' ? 'ascending' : 'descending'}` : ''}
      </span>
    </div>
  )
}
